
import React, { useState, useContext, useEffect } from "react";
import {
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Button,
  Avatar,
  Typography,
  Grid,
  Box,
  CircularProgress,
  IconButton,
  Link,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { toast } from "react-toastify";
import axios from "axios";
import { ContextData } from "../../../../../context/Context";
import baseUrl from "../../../../../BaseURL";

function truncate(text, n = 140) {
  if (!text) return "";
  return text.length > n ? text.slice(0, n) + "..." : text;
}

function GetInstitutionsList() {
  const [documentsList, setDocumentsList] = useState(null);
  const [reload, setReload] = useState(true);
  const { accesstoken } = useContext(ContextData);

  // dialog state for viewing full profile
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function fetchData() {
      try {
        const response = await axios.get(`${baseUrl}/auth/get_uni/dash`, {
          withCredentials: true,
          headers: {
            Accept: "application/json",
            authorization: `Bearer ${accesstoken}`,
          },
        });
        if (mounted) setDocumentsList(response.data || []);
      } catch (err) {
        console.log(err?.message || err);
        if (mounted) setDocumentsList([]);
      }
    }
    fetchData();
    return () => {
      mounted = false;
    };
  }, [reload, accesstoken]);

  const handleDelete = async (token) => {
    if (!window.confirm("Are you sure you want to remove this institution?")) return;
    try {
      const response = await axios.delete(`${baseUrl}/auth/delete_uni/${token}`, {
        withCredentials: true,
        headers: {
          Accept: "application/json",
          authorization: `Bearer ${accesstoken}`,
        },
      });
      toast.warn(response.data.textStatus || "Deleted");
      setReload((r) => !r);
      setSelected(null);
    } catch (err) {
      console.log(err?.message || err);
      toast.error("Failed to delete");
      setReload((r) => !r);
    }
  };

  if (documentsList === null) {
    return (
      <Box className="p-6" display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (!documentsList.length) {
    return (
      <Box className="p-6 bg-gray-100 rounded-lg shadow-lg">
        <Typography variant="h6">No institutions found.</Typography>
      </Box>
    );
  }

  return (
    <Box className="p-6" sx={{ minHeight: 200 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
        Institutions
      </Typography>

      <Grid container spacing={3}>
        {documentsList.map((item, idx) => {
          const initials = (item.university_name || "")
            .split(" ")
            .map((s) => s[0])
            .slice(0, 2)
            .join("")
            .toUpperCase();

          // small info chips
          const chips = [];
          if (item.institutionCategory) chips.push(item.institutionCategory);
          if (item.foreign_or_local) chips.push(item.foreign_or_local);
          if (item.accreditation) chips.push(item.accreditation);

          return (
            <Grid item xs={12} sm={6} md={4} key={item.token || idx}>
              <Card
                elevation={3}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform .2s, box-shadow .2s",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: 8,
                  },
                }}
              >
                <CardHeader
                  avatar={
                    <Avatar
                      sx={{
                        bgcolor: "primary.main",
                        width: 56,
                        height: 56,
                        fontWeight: 700,
                      }}
                      aria-label={`logo-${item.university_name || idx}`}
                    >
                      {initials || "U"}
                    </Avatar>
                  }
                  title={
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      {item.university_name || "Unnamed Institution"}
                    </Typography>
                  }
                  subheader={
                    <Box component="span" sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                      {chips.slice(0, 3).map((c, i) => (
                        <Chip key={i} label={c} size="small" color="primary" sx={{ bgcolor: "" }} />
                      ))}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        #{idx + 1}
                      </Typography>
                    </Box>
                  }
                  action={
                    <IconButton
                      size="small"
                      aria-label="open-website"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.website) {
                          const url = item.website.startsWith("http") ? item.website : `https://${item.website}`;
                          window.open(url, "_blank", "noopener,noreferrer");
                        } else {
                          toast.info("No website available");
                        }
                      }}
                      title="Open website"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  }
                />

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <strong>Address:</strong> {item.ins_address || "N/A"}
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Phone:</strong> {item.phone_number || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      <strong>Email:</strong>{" "}
                      {item.email ? (
                        <Link href={`mailto:${item.email}`} underline="hover">
                          {truncate(item.email, 30)}
                        </Link>
                      ) : (
                        "N/A"
                      )}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <strong>Courses:</strong> {truncate(item.courses_offered, 100) || "N/A"}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    <strong>About:</strong> {truncate(item.about_institution, 110) || "N/A"}
                  </Typography>
                </CardContent>

                <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
                  <Box>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => setSelected(item)}
                      sx={{ textTransform: "none", boxShadow: 0 }}
                    >
                      View profile
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        toast.info("Copied institution token to clipboard");
                        navigator.clipboard?.writeText(item.token || "");
                      }}
                      sx={{ ml: 1, textTransform: "none" }}
                    >
                      Copy ID
                    </Button>
                  </Box>

                  <Box>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(item.token)}
                      title="Remove Institution"
                      aria-label="remove"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Detail dialog */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selected?.university_name || "Institution details"}
          <Typography variant="caption" display="block" color="text.secondary">
            {selected?.institutionCategory} {selected?.foreign_or_local ? `• ${selected.foreign_or_local}` : ""}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Avatar sx={{ bgcolor: "primary.main", width: 80, height: 80, fontSize: 28 }}>
                  {(selected.university_name || "").split(" ").map(s => s[0]).slice(0,2).join("").toUpperCase() || "U"}
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <strong>Accreditation:</strong> {selected.accreditation || "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <strong>Phone:</strong> {selected.phone_number || "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <strong>Email:</strong>{" "}
                    {selected.email ? (
                      <Link href={`mailto:${selected.email}`} underline="hover">
                        {selected.email}
                      </Link>
                    ) : (
                      "N/A"
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <strong>Website:</strong>{" "}
                    {selected.website ? (
                      <Link href={selected.website.startsWith("http") ? selected.website : `https://${selected.website}`} target="_blank" rel="noreferrer" underline="hover">
                        {selected.website}
                      </Link>
                    ) : (
                      "N/A"
                    )}
                  </Typography>
                </Box>
              </Stack>

              <Box mt={2}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>About</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {selected.about_institution || "No additional information."}
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Courses offered</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {selected.courses_offered || "No courses listed."}
                </Typography>

                {selected.other_institution ? (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Other</Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {selected.other_institution}
                    </Typography>
                  </>
                ) : null}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} size="small">
            Close
          </Button>
          <Button color="error" onClick={() => selected && handleDelete(selected.token)} size="small">
            Remove
          </Button> 
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default GetInstitutionsList;
